/**
 * Signature styles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const signatureStylesPack: ToilePack = {
    slug: 'signature-styles',
    category: 'illustration',
    name: 'Signature styles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'options', label: 'Six options', subject: 'a sheet with the name "Anna Patel" signed six ways down the page, each on its own baseline, from a full readable script to a short looped initial mark'},
        {key: 'initials', label: 'Initials signature', subject: 'a sheet with the initials "J.R." signed in four styles, a large looped J, an underline stroke and a short flourish, one per row'},
        {key: 'practice', label: 'Practice rows', subject: 'a practice sheet with the signature "David Moreau" written once in black at the top, then repeated in light grey on six rows to trace'},
    ],
    styles: [
        {key: 'clean', label: 'Clean and legible', style: 'a clean upright signature, the letters joined and easy to read', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'flowing', label: 'Flowing', style: 'a flowing signature with a large looped capital, a long tail stroke and a sweeping underline', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'minimal', label: 'Minimal', style: 'a minimal signature, a few quick strokes, short and simple', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
    ],
    directions: ['a short name', 'initials only', 'with a dash'],
    directionHint: 'try: a short name',
    seo: {
        title: 'Signature Styles: Six Ways to Sign a Name',
        metaDescription: 'Nine signature style sheets: a name signed clean, flowing and minimal, with initials and practice rows, in ink.',
        h1: 'Signature styles: six ways to sign a name, initials, practice rows',
        tagline: 'Ink signatures written on lined rows, ready to compare and copy.',
        intro: 'A signature idea is the same name signed several ways, so you can pick one and practice it. The nine sheets show Anna Patel signed six ways, the initials J.R. in four styles, and David Moreau repeated in light ink on lined rows. Each comes as a clean upright hand, a flowing hand with a looped capital, or a minimal few-stroke form. Ask for a short name, initials only or a dash.',
        useCases: [
            'choosing a signature for a letter or a card',
            'a practice sheet to repeat a signature',
            'a signature for a card',
            'a sketchbook study of handwriting',
        ],
        faq: [
            {q: 'Are these legal signatures?', a: 'No. They are design ideas for a handwritten signature, not a signed form.'},
            {q: 'What are the three styles?', a: 'Clean is an upright signature with joined, easy-to-read letters. Flowing has a large looped capital, a long tail and a sweeping underline. Minimal is a few quick strokes.'},
            {q: 'How do I practice one?', a: 'The practice rows show the signature once in dark ink at the top, then repeated in light ink below it.'},
            {q: 'Can I get my own name?', a: 'Yes. Ask for a short name, initials only or a dash, and the comparison is set for that.'},
        ],
    },
}
